import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, FormControl, FormHelperText, FormLabel, Input, Spinner, Text } from '@chakra-ui/react'
import { TicketService } from '@/lib/services/ticket'
import type { TeamOption } from '@/types/ticket'
import { useThemeColors } from '@/hooks/useThemeColors'

type Props = {
  label?: string
  value?: number
  onChange: (teamId?: number, team?: TeamOption) => void
  helperText?: string
  placeholder?: string
  hideLabel?: boolean
  showSelectedLine?: boolean
  inputBaseProps?: Record<string, unknown>
}

export function TeamSearchSelect({
  label,
  value,
  onChange,
  helperText,
  placeholder = 'Search teams...',
  hideLabel = false,
  showSelectedLine = true,
  inputBaseProps,
}: Props) {
  const colors = useThemeColors()
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<TeamOption[]>([])
  const [selected, setSelected] = useState<TeamOption | null>(null)
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const selectedRef = useRef<TeamOption | null>(null)

  useEffect(() => {
    selectedRef.current = selected
  }, [selected])

  useEffect(() => {
    if (!value) {
      setSelected(null)
      setSearchTerm('')
      return
    }
    let cancelled = false
    TicketService.searchTeamOptions('').then((res) => {
      if (cancelled || !res.success || !res.data) return
      const match = res.data.find((team) => team.id === value)
      if (match) {
        setSelected(match)
        setSearchTerm(match.name)
      }
    })
    return () => {
      cancelled = true
    }
  }, [value])

  const closeList = useCallback((restoreSelected = true) => {
    setIsOpen(false)
    setResults([])
    if (!restoreSelected) return
    const current = selectedRef.current
    setSearchTerm(current?.name ?? '')
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (containerRef.current && target && !containerRef.current.contains(target)) {
        closeList(true)
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeList(true)
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, closeList])

  const searchTeams = useCallback(async (term: string) => {
    setLoading(true)
    try {
      const res = await TicketService.searchTeamOptions(term)
      setResults(res.success && res.data ? res.data : [])
      setIsOpen(true)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value
    setSearchTerm(term)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    if (!term.trim()) {
      setResults([])
      setIsOpen(false)
      if (selected) {
        setSelected(null)
        onChange(undefined, undefined)
      }
      return
    }

    timeoutRef.current = setTimeout(() => {
      void searchTeams(term)
    }, 250)
  }

  const handleSelect = (team: TeamOption) => {
    setSelected(team)
    setSearchTerm(team.name)
    setResults([])
    setIsOpen(false)
    onChange(team.id, team)
  }

  return (
    <FormControl>
      {!hideLabel && label ? <FormLabel fontSize="sm">{label}</FormLabel> : null}
      <Box ref={containerRef} position="relative">
        <Input
          size="sm"
          value={searchTerm}
          onChange={handleChange}
          onFocus={() => {
            void searchTeams(searchTerm)
          }}
          placeholder={placeholder}
          autoComplete="off"
          {...inputBaseProps}
        />
        {loading && (
          <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
            <Spinner size="sm" color={colors.primary} />
          </Box>
        )}
        {isOpen && results.length > 0 && !loading && (
          <Box
            position="absolute"
            zIndex={10}
            w="100%"
            mt={1}
            bg="white"
            border="1px solid"
            borderColor={colors.borderLight}
            borderRadius="md"
            boxShadow="md"
            maxH="240px"
            overflowY="auto"
          >
            {results.map((team) => (
              <Box
                key={team.id}
                px={4}
                py={2}
                cursor="pointer"
                _hover={{ bg: 'gray.100' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(team)}
              >
                <Text fontWeight="600" fontSize="sm">
                  {team.name}
                </Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {selected && showSelectedLine && (
        <Text fontSize="sm" color={colors.text} mt={2}>
          Selected: {selected.name}
        </Text>
      )}
      {helperText ? <FormHelperText fontSize="xs">{helperText}</FormHelperText> : null}
    </FormControl>
  )
}
